// @givmo/board — "Add a committee" (CBG-YR6-REG, frame c3; `board-committees.md` §4.1).
//
// One dialog for the moment someone adds a committee: its name, standing or ad hoc, when it was
// established and where that is recorded, and the neutral charter form its charter starts from. The
// form library's rails hold (plan §3.2 Band 2, the version dialog's header): the person picks a form,
// Givmo picks none and fills none, and a chosen form shows its review line and sources. "No charter
// yet" is the default. With a form, the committee holds its charter's label (its own name) and the
// committee's page opens the version dialog on that form — unless a charter is already recorded under
// that name, which the committee then holds as it stands (§4.1: the create writes no record).
//
// What it never asks: whether the committee can act for the board. That is line 8b's question, asked
// on the committee's own page with its members and charter beside it (frame c6).
//
// CBG-YR6-CONFIRM (Q3; frame c3's caption, though the frame shows another form): choosing the
// executive committee charter form shows one checkbox, ticked before anything is saved and one click
// off — "Suggest its decisions for the board's next meeting", with the form's note. Any other choice
// shows none (a committee turns the practice on from its own page). The default is this dialog's,
// from the form chosen: the backend reads nothing from the form to set it, so the create carries
// `confirm_with_board` itself.

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardCommitteeContinuity,
  type BoardCommitteeOut,
  type BoardCommitteeRecordedIn,
  type BoardGoverningDocumentTemplateOut,
} from '@givmo/api-client';
import {
  Button,
  Checkbox,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Select,
  TextField,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { TemplateNotice } from '../consents/template-notice';
import { extractGoverningDocumentTemplates } from '../govdocs/govdocs-decode';
import { RecordedInPicker } from './recorded-in-picker';
import { committeeConfirmationWords as cw } from './committee-confirmation-words';
import { committeesWords as w } from './committees-words';

const NO_CHARTER = 'none';
/** The executive committee charter form (FORMS' id, pinned by the forms' golden): the one whose
 *  committee starts with the practice ticked (Q3). */
const EXECUTIVE_FORM = 'committee-charter-executive';

export function AddCommitteeDialog({
  orgRef,
  onClose,
  onAdded,
}: {
  orgRef: string;
  onClose: () => void;
  /** The new committee, and the form its charter starts from (or null). */
  onAdded: (committee: BoardCommitteeOut, formId: string | null) => void;
}) {
  const operations = useBoardOperations();
  const [name, setName] = useState('');
  const [continuity, setContinuity] = useState<BoardCommitteeContinuity>('standing');
  const [established, setEstablished] = useState('');
  const [recordedIn, setRecordedIn] = useState<BoardCommitteeRecordedIn | null>(null);
  const [form, setForm] = useState(NO_CHARTER);
  const [confirm, setConfirm] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['board-governing-document-templates', orgRef],
    queryFn: async (): Promise<BoardGoverningDocumentTemplateOut[]> => {
      const { data } = await operations.officerListBoardGoverningDocumentTemplates({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return extractGoverningDocumentTemplates(data);
    },
  });
  const forms = useMemo(
    () =>
      (templatesQuery.data ?? []).filter(
        (template) => template.document_kind === 'committee_charter',
      ),
    [templatesQuery.data],
  );
  const chosen = forms.find((template) => template.template_id === form) ?? null;
  const offersPractice = chosen?.template_id === EXECUTIVE_FORM;

  const add = useMutation({
    mutationFn: async (): Promise<BoardCommitteeOut> => {
      const { data } = await operations.officerCreateBoardCommittee({
        path: { org_ref: orgRef },
        body: {
          name: name.trim(),
          continuity,
          ...(established.length > 0 ? { established_on: established } : {}),
          ...(recordedIn !== null ? { recorded_in: recordedIn } : {}),
          ...(chosen !== null ? { charter_form_id: chosen.template_id } : {}),
          ...(offersPractice && confirm ? { confirm_with_board: true } : {}),
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (committee) => {
      toast.success(w.added(committee.name));
      onAdded(committee, chosen?.template_id ?? null);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : w.addFailed),
  });

  const pending = add.isPending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onClose()}>
      <Dialog.Content
        aria-label={w.addTitle}
        className="flex flex-col gap-16 overflow-hidden"
        testId="committee-add-dialog"
      >
        <Dialog.Title className="shrink-0 font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {w.addTitle}
        </Dialog.Title>

        <div
          className="-mx-4 flex min-h-0 flex-1 flex-col gap-16 overflow-y-auto overscroll-contain px-4 py-2"
          data-testid="committee-add-body"
        >
          <TextField
            label={w.nameLabel}
            value={name}
            maxLength={120}
            disabled={pending}
            onChange={(event) => setName(event.target.value)}
            testId="committee-add-name"
          />

          <RadioTileGroup
            value={continuity}
            onValueChange={(next) => setContinuity(next === 'ad_hoc' ? 'ad_hoc' : 'standing')}
            aria-label={w.kindLabel}
            className="grid grid-cols-2 gap-10 max-sm:grid-cols-1"
            testId="committee-add-kind"
          >
            <RadioTile
              value="standing"
              label={w.standing}
              description={w.standingNote}
              testId="committee-add-standing"
            />
            <RadioTile
              value="ad_hoc"
              label={w.adHoc}
              description={w.adHocNote}
              testId="committee-add-ad-hoc"
            />
          </RadioTileGroup>

          <TextField
            label={w.establishedLabel}
            type="date"
            value={established}
            disabled={pending}
            onChange={(event) => setEstablished(event.target.value)}
            testId="committee-add-established"
          />
          <RecordedInPicker
            orgRef={orgRef}
            value={recordedIn}
            onChange={setRecordedIn}
            disabled={pending}
            testId="committee-add-recorded-in"
          />

          <div className="flex flex-col gap-8">
            <Select
              label={w.formLabel}
              appearance="saas"
              value={form}
              disabled={pending}
              onValueChange={(next) => setForm(next ?? NO_CHARTER)}
              options={[
                { value: NO_CHARTER, label: w.noCharterYet },
                ...forms.map((template) => ({
                  value: template.template_id,
                  label: template.name ?? template.template_id,
                })),
              ]}
              testId="committee-add-form"
            />
            {chosen !== null ? (
              <TemplateNotice
                reviewLine={chosen.review_line ?? ''}
                provenance={chosen.provenance}
                sourceCitations={chosen.source_citations}
                testId="committee-add-form-notice"
              />
            ) : null}
          </div>

          {offersPractice ? (
            <div className="flex flex-col gap-6">
              <label className="flex items-start gap-10 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                <Checkbox
                  appearance="saas"
                  checked={confirm}
                  disabled={pending}
                  onCheckedChange={(next) => setConfirm(next === true)}
                  testId="committee-add-confirm"
                />
                {cw.confirmLabel}
              </label>
              <FieldNote>{cw.confirmNote}</FieldNote>
            </div>
          ) : null}
        </div>

        {chosen !== null ? (
          <p className="shrink-0 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            {w.formClosing}
          </p>
        ) : null}

        {error !== null ? <ErrorMessage testId="committee-add-error">{error}</ErrorMessage> : null}

        <div className="flex shrink-0 justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={pending}
            onClick={onClose}
            testId="committee-add-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={pending}
            disabled={pending || name.trim().length === 0}
            onClick={() => {
              setError(null);
              add.mutate();
            }}
            testId="committee-add-save"
          >
            {w.addSave}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
